/* eslint-disable react/no-unescaped-entities */
/**
 * Coaching Client Dashboard — hub for the 3 Healing Trilogy programs.
 * Route: /coaching/dashboard
 *
 * Client enters email → we call /api/coaching/dashboard/for-client and
 * render 3 program cards with clear lock states. Clicking an unlocked
 * enrolled program routes to its passcode login (scoped to that program).
 * Trilogy certificate appears when all three show program_completed=True.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, CheckCircle2, ArrowRight, Award } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PROGRAM_META = {
  "the-me-i-see": {
    title: "The Me I See",
    tagline: "Program 01 · Identity Awareness · 8 Weeks",
    login: "/coaching/login",  // legacy me-i-see auth route
    appPath: "/coaching/me-i-see",
    sales: "/coaching/the-me-i-see",
    weeksLabel: "8 weeks",
  },
  "healing-what-you-cannot-feel": {
    title: "Healing What You Cannot Feel",
    tagline: "Program 02 · Emotional Healing · 12 Weeks",
    login: "/coaching/hwycf/login",
    appPath: "/coaching/healing-what-you-cannot-feel/app",
    sales: "/coaching/healing-what-you-cannot-feel",
    weeksLabel: "12 weeks",
  },
  "she-is": {
    title: "She Is",
    tagline: "Program 03 · Arrival & Embodiment · 12 Weeks",
    login: "/coaching/she-is/login",
    appPath: "/coaching/she-is/app",
    sales: "/coaching/she-is",
    weeksLabel: "12 weeks",
  },
};

export default function CoachingDashboard() {
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function lookup(e) {
    e && e.preventDefault();
    if (!email) return;
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${API}/api/coaching/dashboard/for-client`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setData(await r.json());
      localStorage.setItem("coaching_dashboard_email", email.trim().toLowerCase());
    } catch { setErr("Could not load your dashboard. Try again."); }
    setLoading(false);
  }

  return (
    <div data-testid="coaching-dashboard" className="min-h-screen" style={{ background: "#f6f4f0", fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "#5B2C83", fontWeight: 600 }}>
          Christy Vonta Coaching · KCIS Coaching Door
        </div>
        <h1 className="mt-2 text-4xl sm:text-5xl" style={{ fontFamily: "'Playfair Display', serif", color: "#111" }}>
          Your Healing Trilogy
        </h1>
        <p className="mt-3 text-slate-700 max-w-2xl">
          Three programs, thirty-two weeks, one woman — fully seen, fully healed, fully arrived. Each program unlocks after the one before it is complete.
        </p>

        {!data && (
          <form onSubmit={lookup} className="mt-8 flex gap-2 max-w-lg" data-testid="dashboard-email-form">
            <input
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter the email you enrolled with"
              className="flex-1 rounded-full border px-5 py-3 text-sm focus:outline-none focus:border-purple-900"
              style={{ borderColor: "#ddd", background: "white" }}
              data-testid="dashboard-email-input"
            />
            <button disabled={loading} type="submit"
                    className="rounded-full px-6 py-3 text-sm font-medium text-white disabled:opacity-40"
                    style={{ background: "#5B2C83" }} data-testid="dashboard-lookup-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
            </button>
          </form>
        )}

        {err && <div className="mt-4 text-sm text-red-700">{err}</div>}

        {data && (
          <>
            <div className="mt-2 text-xs text-slate-500">Signed in as {data.email} · <button className="underline" onClick={() => { setData(null); setEmail(""); }} data-testid="dashboard-change-email">use a different email</button></div>

            <div className="mt-8 grid gap-4">
              {data.programs.map((p) => {
                const meta = PROGRAM_META[p.program_slug];
                const done = p.program_completed;
                const locked = !p.content_unlocked && !p.enrolled;
                const gated = p.enrolled && !p.content_unlocked;
                const canOpen = p.enrolled && p.content_unlocked;
                return (
                  <div key={p.program_slug}
                       data-testid={`dashboard-card-${p.program_slug}`}
                       className="rounded-2xl border p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5"
                       style={{
                         background: done ? "#f0f6ec" : "white",
                         borderColor: done ? "#7FA66A" : gated || locked ? "#ddd" : "#5B2C83",
                         opacity: locked || gated ? 0.85 : 1,
                       }}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                         style={{
                           background: done ? "#7FA66A" : canOpen ? "#5B2C83" : "#eee",
                           color: done || canOpen ? "white" : "#999",
                         }}>
                      {done ? <CheckCircle2 className="w-5 h-5" /> :
                       locked || gated ? <Lock className="w-5 h-5" /> :
                       <ArrowRight className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{meta.tagline}</div>
                      <div className="text-xl mt-1" style={{ fontFamily: "'Playfair Display', serif", color: "#111" }}>
                        {meta.title}
                      </div>
                      {done && <div className="text-xs mt-1" style={{ color: "#7FA66A" }}>Completed — Certificate available</div>}
                      {gated && <div className="text-xs mt-1 text-slate-500">
                        Unlocks after completing {PROGRAM_META[p.prereq_slug].title}
                      </div>}
                      {!p.enrolled && !canOpen && <div className="text-xs mt-1 text-slate-500">Not yet enrolled</div>}
                      {canOpen && !done && p.progress_pct > 0 && (
                        <div className="text-xs mt-1 text-slate-500">In progress — Module {p.current_module} of {meta.weeksLabel === "8 weeks" ? 8 : 12} · {p.progress_pct}%</div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {canOpen ? (
                        <Link to={meta.login} data-testid={`dashboard-open-${p.program_slug}`}>
                          <button className="rounded-full px-5 py-2.5 text-xs font-medium text-white"
                                  style={{ background: done ? "#7FA66A" : "#5B2C83" }}>
                            {done ? "Review + Certificate" : "Continue"}
                          </button>
                        </Link>
                      ) : (
                        <Link to={meta.sales} data-testid={`dashboard-learn-${p.program_slug}`}>
                          <button className="rounded-full px-5 py-2.5 text-xs border" style={{ borderColor: "#5B2C83", color: "#5B2C83" }}>
                            Learn more
                          </button>
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {data.trilogy_complete && (
              <div className="mt-10 rounded-2xl border p-6 sm:p-8"
                   style={{ background: "linear-gradient(135deg,#0d0510 0%,#3d1a54 45%,#5B2C83 100%)",
                            borderColor: "rgba(127,166,106,0.35)" }}
                   data-testid="dashboard-trilogy-cta">
                <div className="text-[10px] uppercase tracking-[0.35em]" style={{ color: "#9CD87E", fontWeight: 600 }}>
                  The Healing Trilogy Is Complete
                </div>
                <div className="text-white text-2xl mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Your master certificate is ready.
                </div>
                <Link to="/coaching/trilogy/certificate" data-testid="dashboard-trilogy-cert-btn">
                  <button className="mt-4 rounded-full px-6 py-3 text-sm font-medium text-white inline-flex items-center gap-2"
                          style={{ background: "linear-gradient(135deg,#7FA66A,#5B2C83)" }}>
                    <Award className="w-4 h-4" /> View Trilogy Certificate
                  </button>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
